/**
 * Cart Queue Manager
 * Ensures ALL cart operations are processed sequentially to prevent race conditions
 * and maintain cart consistency across the entire application
 */

class CheckoutCartManager {
  constructor() {
    this.checkoutId = null;
    this.isActive = false;

    // Request queue for sequential processing
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.queueProcessingPromise = null;

    this.initializeManager();
  }

  initializeManager() {
    console.log('🛒 Initializing Checkout Cart Manager...');

    // Check if checkout is active on page load
    this.checkActiveCheckout();

    // Listen for checkout events
    document.addEventListener('checkout:started', (e) => this.handleCheckoutStarted(e.detail));
    document.addEventListener('checkout:completed', (e) => this.handleCheckoutCompleted(e.detail));
    document.addEventListener('checkout:cancelled', (e) => this.handleCheckoutCancelled(e.detail));

    // Override cart operations when checkout is active
    this.setupCartOperationOverrides();

    console.log('✅ Checkout Cart Manager initialized');
  }

  /**
   * Check if checkout is currently active
   */
  checkActiveCheckout() {
    // Get checkoutId from localStorage
    const storedCheckoutId = localStorage.getItem('checkoutId');

    // Check if checkout component indicates active checkout
    const checkoutPresent = document.querySelector('body').dataset.checkoutPresent === 'true';

    // Also check if we're on the checkout page
    const onCheckoutPage =
      window.location.pathname.includes('/checkout') || document.querySelector('.checkout-component') !== null;

    // Checkout is active if we have an ID AND (checkout present OR on checkout page)
    if (storedCheckoutId && (checkoutPresent || onCheckoutPage)) {
      this.checkoutId = storedCheckoutId;
      this.isActive = true;
      console.log(`🛒 Active checkout detected on page load: ${this.checkoutId}`);
    } else {
      // Clear any stale checkout data
      if (storedCheckoutId && !checkoutPresent && !onCheckoutPage) {
        console.log('🛒 Clearing stale checkout data from localStorage');
        localStorage.removeItem('checkoutId');
      }
      this.checkoutId = null;
      this.isActive = false;
    }
  }

  /**
   * Handle checkout started event
   */
  handleCheckoutStarted(detail) {
    this.checkoutId = detail.checkoutId;
    this.isActive = true;
    localStorage.setItem('checkoutId', this.checkoutId);
    console.log(`🛒 Checkout started: ${this.checkoutId}`);
  }

  /**
   * Handle checkout completed event
   */
  handleCheckoutCompleted(detail) {
    this.checkoutId = null;
    this.isActive = false;
    localStorage.removeItem('checkoutId');

    // Clear any pending requests in the queue
    if (this.requestQueue.length > 0) {
      console.log(`🗑️ Clearing ${this.requestQueue.length} pending requests due to checkout completion`);
      this.requestQueue.forEach((request) => {
        request.reject(new Error('Checkout completed - request cancelled'));
      });
      this.requestQueue = [];
    }

    console.log('🛒 Checkout completed, resuming normal cart operations');
  }

  /**
   * Handle checkout cancelled event
   */
  handleCheckoutCancelled(detail) {
    this.checkoutId = null;
    this.isActive = false;
    localStorage.removeItem('checkoutId');

    // Clear any pending requests in the queue
    if (this.requestQueue.length > 0) {
      console.log(`🗑️ Clearing ${this.requestQueue.length} pending requests due to checkout cancellation`);
      this.requestQueue.forEach((request) => {
        request.reject(new Error('Checkout cancelled - request cancelled'));
      });
      this.requestQueue = [];
    }

    console.log('🛒 Checkout cancelled, resuming normal cart operations');
  }

  /**
   * Setup overrides for cart operations - ALL cart operations are queued
   */
  setupCartOperationOverrides() {
    // Store original fetch for fallback
    if (!window.originalFetch) {
      window.originalFetch = window.fetch;
    }

    // Override fetch to intercept cart operations
    const manager = this;
    window.fetch = async function (url, options = {}) {
      const shouldIntercept = manager.shouldInterceptRequest(url);

      // Queue ALL cart operations unconditionally
      if (shouldIntercept && !manager.isActive) {
        console.log('🛒 CheckoutCartManager: Enqueueing cart request:', url);
        console.log('🛒 CheckoutCartManager: Queue size before:', manager.requestQueue.length);

        // All cart operations go through the queue
        return manager.handleCartOperation(url, options);
      }

      // Non-cart requests use original fetch
      return window.originalFetch(url, options);
    };
  }

  /**
   * Check if request should be intercepted
   */
  shouldInterceptRequest(url) {
    const cartEndpoints = ['/cart/add.js', '/cart/update.js', '/cart/change.js', '/cart/clear.js'];

    // Handle both string URLs and URL objects
    const urlString = url instanceof URL ? url.href : url.toString();

    return cartEndpoints.some((endpoint) => urlString.includes(endpoint));
  }

  /**
   * Add request to queue and process (with smart merging for add operations)
   */
  async enqueueRequest(requestFn, requestName = 'cart-operation', requestData = null) {
    return new Promise((resolve, reject) => {
      // Check if this is an add operation that can be merged
      if (requestName.startsWith('cart-add') && requestData) {
        const existingAddIndex = this.requestQueue.findIndex(
          (req) => req.name.startsWith('cart-add') && !req.processing
        );

        if (existingAddIndex !== -1) {
          // Merge with existing add request
          console.log(`🔀 Merging add request with existing queued add operation`);
          const existingRequest = this.requestQueue[existingAddIndex];

          // Merge the items arrays
          if (existingRequest.data && existingRequest.data.items) {
            existingRequest.data.items = existingRequest.data.items.concat(requestData.items || []);
            console.log(`📦 Combined add request now has ${existingRequest.data.items.length} items`);

            // Add this promise to be resolved when the merged request completes
            if (!existingRequest.additionalResolvers) {
              existingRequest.additionalResolvers = [];
            }
            existingRequest.additionalResolvers.push({ resolve, reject });

            return; // Don't add a new request, just merge with existing
          }
        }
      }

      console.log(`📥 Enqueueing request: ${requestName}, Queue size: ${this.requestQueue.length + 1}`);

      // Add request to queue
      this.requestQueue.push({
        fn: requestFn,
        name: requestName,
        data: requestData,
        resolve,
        reject,
        timestamp: Date.now(),
        processing: false,
      });

      // Start processing if not already processing
      if (!this.isProcessingQueue) {
        this.processQueue();
      }
    });
  }

  /**
   * Process the request queue sequentially
   */
  async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    console.log('🔄 Starting queue processing...');

    while (this.requestQueue.length > 0) {
      const request = this.requestQueue.shift();
      request.processing = true;
      const waitTime = Date.now() - request.timestamp;

      // Log merged request info if applicable
      if (request.additionalResolvers && request.additionalResolvers.length > 0) {
        console.log(
          `⏳ Processing MERGED: ${request.name} (${
            request.additionalResolvers.length + 1
          } combined requests, waited ${waitTime}ms)`
        );
      } else {
        console.log(`⏳ Processing: ${request.name} (waited ${waitTime}ms, ${this.requestQueue.length} remaining)`);
      }

      try {
        // If this is a merged add request, create a new function with combined items
        let result;
        if (request.name.startsWith('cart-add') && request.data && request.data.items) {
          console.log(`📦 Executing merged add with ${request.data.items.length} items`);
          result = await this.executeMergedAdd(request.data.items);
        } else {
          result = await request.fn();
        }

        if (result && result['items']) {
          result['items'].forEach(item => {
            const quantityInputs = document.querySelectorAll(`quantity-input[data-variant-id="${item.variant_id}"]`);
            quantityInputs.forEach(quantityInput => {
              quantityInput.classList.add('in-cart')
            })
          });
        }
      
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && result && result['sections'] && result['sections']['cart-icon-bubble']) {
          cartIconBubble.innerHTML = result['sections']['cart-icon-bubble'];
          // const cartCountElement = cartIconBubble.querySelector('cart-count');
          // if (cartCountElement) {
          //   // If there's a custom element, dispatch the event instead of replacing HTML
          //   document.dispatchEvent(new CustomEvent('cart:on:update:sections', {
          //     detail: {
          //       'cart-icon-bubble': data.sections[section.section],
          //       source: 'cart-drawer',
          //       timestamp: Date.now(),
          //       trusted: true
          //     }
          //   }));
          // }
        }

        // Resolve the main promise
        request.resolve(result);

        // Resolve any additional promises from merged requests
        if (request.additionalResolvers) {
          request.additionalResolvers.forEach((resolver) => {
            resolver.resolve(result);
          });
          console.log(
            `✅ Completed merged request: ${request.name} (resolved ${request.additionalResolvers.length + 1} promises)`
          );
        } else {
          console.log(`✅ Completed: ${request.name}`);

          //!request.name.includes(`cart-clear`)
          if(SharedCart && window.customer){
            const sharedCart = new SharedCart();
            console.log(`⏳ Calling SharedCart.handleCartChange()`)
            await sharedCart.handleCartChange();
          }
        }

        // Small delay between requests to prevent overwhelming the server
        if (this.requestQueue.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`❌ Failed: ${request.name}`, error);
        request.reject(error);

        // Reject any additional promises from merged requests
        if (request.additionalResolvers) {
          request.additionalResolvers.forEach((resolver) => {
            resolver.reject(error);
          });
        }
      }
    }

    this.isProcessingQueue = false;
    console.log('✅ Queue processing complete');
  }

  /**
   * Execute a merged add request with multiple items
   */
  async executeMergedAdd(items) {
    console.log(`🛒 Executing merged add with ${items.length} items`);
    const response = await window.originalFetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items, sections: 'cart-icon-bubble' }),
    });

    if (!response.ok) {
      throw new Error(`Merged add operation failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Handle cart operation - ALL cart operations are queued
   */
  async handleCartOperation(url, options) {
    console.log(`🔄 Queueing cart operation: ${url}`);

    // Determine operation type
    let operationType = 'update';
    let requestData = null;

    if (url.includes('/cart/add.js')) {
      operationType = 'add';

      // Parse the request body to extract items for potential merging
      try {
        if (options && options.body) {
          if (typeof options.body === 'string') {
            requestData = JSON.parse(options.body);
          } else if (options.body instanceof FormData) {
            // Handle FormData (convert to object)
            const formObj = {};
            for (let [key, value] of options.body.entries()) {
              formObj[key] = value;
            }
            requestData = formObj;
          }
        }
      } catch (e) {
        console.warn('Could not parse add request body for merging:', e);
      }
    } else if (url.includes('/cart/change.js')) {
      operationType = 'change';
    } else if (url.includes('/cart/clear.js')) {
      operationType = 'clear';
    }

    // Enqueue request with data for potential merging
    return this.enqueueRequest(
      async () => {
        // Fix headers for FormData - remove Content-Type so browser sets it correctly
        let adjustedOptions = options;
        if (options && options.body instanceof FormData && options.headers) {
          adjustedOptions = {
            ...options,
            headers: {
              ...options.headers
            }
          };
          // Remove Content-Type header for FormData - browser will set it with boundary
          delete adjustedOptions.headers['Content-Type'];
          delete adjustedOptions.headers['content-type'];
        }
        
        // Use the normal Shopify cart API
        const response = await window.originalFetch(url, adjustedOptions);

        if (!response.ok) {
          // Try to get error details from response
          let errorDetails = '';
          try {
            const errorBody = await response.clone().text();
            errorDetails = ` - ${errorBody}`;
          } catch (e) {
            // Ignore if we can't read the error body
          }
          
          console.error(`❌ Cart ${operationType} operation failed:`, {
            status: response.status,
            url: url,
            data: requestData,
            error: errorDetails
          });
          
          throw new Error(`Cart operation failed: ${response.status}${errorDetails}`);
        }

        // Log the operation completion
        console.log(`✅ Cart ${operationType} operation completed`);

        return response;
      },
      `cart-${operationType}`,
      requestData
    );
  }

  /**
   * Update cart item (used by cart-items.js)
   */
  async updateCartItem(lineItemKey, quantity, properties) {
    // Enqueue the request for sequential processing
    return this.enqueueRequest(async () => {
      // Use Shopify's cart/change.js endpoint
      const response = await window.originalFetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: quantity,
          properties: properties,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update cart item: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Updated item ${lineItemKey} to quantity ${quantity}`);
      return data;
    }, `update-item-${lineItemKey}-qty-${quantity}`);
  }

  /**
   * Add items to cart
   */
  async addToCart(items) {
    // Enqueue the request for sequential processing
    return this.enqueueRequest(async () => {
      // Use Shopify's cart/add.js endpoint
      const response = await window.originalFetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add items: ${response.status}`);
      }

      const data = await response.json();
      console.log(`✅ Added ${items.length} items to cart`);
      return data;
    }, `add-items-${items.length}`);
  }

  /**
   * Clear cart
   */
  async clearCart() {
    // Enqueue the request for sequential processing
    return this.enqueueRequest(async () => {
      // Use Shopify's cart/clear.js endpoint
      const response = await window.originalFetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to clear cart: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Cart cleared');
      return data;
    }, 'clear-cart');
  }
}

// Initialize the checkout cart manager
window.checkoutCartManager = new CheckoutCartManager();

// Export utility functions
window.isCheckoutActive = () => {
  return window.checkoutCartManager.isActive;
};

window.getActiveCheckoutId = () => {
  return window.checkoutCartManager.checkoutId;
};

// Queue monitoring utilities
window.getCartQueueStatus = () => {
  const manager = window.checkoutCartManager;
  return {
    queueLength: manager.requestQueue.length,
    isProcessing: manager.isProcessingQueue,
    requests: manager.requestQueue.map((r) => ({
      name: r.name,
      waitTime: Date.now() - r.timestamp,
      isMerged: !!(r.additionalResolvers && r.additionalResolvers.length > 0),
      mergedCount: r.additionalResolvers ? r.additionalResolvers.length + 1 : 1,
      itemCount: r.data && r.data.items ? r.data.items.length : null,
    })),
  };
};

window.clearCartQueue = () => {
  const manager = window.checkoutCartManager;
  const cleared = manager.requestQueue.length;
  manager.requestQueue.forEach((request) => {
    request.reject(new Error('Queue cleared by user'));
  });
  manager.requestQueue = [];
  console.log(`🗑️ Cleared ${cleared} pending requests from queue`);
  return cleared;
};

console.log('✅ Cart Queue Manager loaded');
console.log('🔧 ALL cart operations are queued and processed sequentially');
console.log('📊 Use window.getCartQueueStatus() to monitor queue');
console.log('🗑️ Use window.clearCartQueue() to clear pending requests');
